import 'expo-dev-client' // Attaches improved error handling to development builds.
// This needs to be at the top of the App.{js|tsx} file. See
// https://docs.expo.dev/develop/development-builds/use-development-builds/#add-error-handling
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useFonts } from 'expo-font'
import * as SplashScreen from 'expo-splash-screen'
import { StatusBar } from 'expo-status-bar'
import React, { Suspense, useEffect, useMemo } from 'react'
import { SWRConfig, SWRConfiguration } from 'swr'
import { subscribe } from 'valtio'

import AppLoading from './components/AppLoading'
import { ErrorBoundary } from './components/ErrorBoundary'
import { ForceUpdateApp } from './components/ForceUpdateApp/ForceUpdateApp'
import { ENVIRONMENT } from './constants/config'
import { AuthProvider } from './context/auth'
import { CartProvider } from './context/cart'
import { ConfigProvider } from './context/config'
import { DiscountsProvider } from './context/discounts'
import { LocalesProvider } from './context/locales'
import DesignSystemProvider from './design-system/DesignSystemProvider'
import { fontFamily } from './design-system/theme/partners/tokens'
import { useAppStatus } from './hooks/use-app-status'
import fetcher from './lib/fetcher'
import { isMountedRef } from './lib/refs'
import Navigation from './navigation'
import './services/i18n'
import './style.css'
import mixpanelInstance from './util/mixpanel'
import { initSentry } from './util/sentry'

// Keep the splash screen visible while we fetch resources
SplashScreen.preventAutoHideAsync().then()

initSentry()

export default function App() {
  const { status } = useAppStatus()
  const swrConfig = useMemo<SWRConfiguration>(() => {
    return { fetcher: fetcher.api }
  }, [])

  // TODO: check how can we allocate these fonts into their own partners so we will need only to import the right one.
  const [fontsLoaded] = useFonts(
    fontFamily === 'Helvetica'
      ? { Helvetica: require(`./fonts/Helvetica.ttf`), 'Helvetica-Bold': require(`./fonts/Helvetica-Bold.ttf`) }
      : { Axiforma: require('./fonts/Axiforma.ttf'), 'Axiforma-Bold': require('./fonts/Axiforma-Extra-Bold.ttf') }
  )

  subscribe(status, () => {
    if (status.isReady) {
      SplashScreen.hideAsync().then()
    }
  })

  useEffect(() => {
    if (ENVIRONMENT === 'prod') {
      mixpanelInstance.init().then()
    }

    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  if (!fontsLoaded) {
    return null
  }

  const queryClient = new QueryClient()

  return (
    <DesignSystemProvider>
      <SWRConfig value={swrConfig}>
        <CartProvider>
          <ConfigProvider>
            <AuthProvider>
              <ErrorBoundary>
                <Suspense fallback={<AppLoading />}>
                  <LocalesProvider>
                    <ForceUpdateApp>
                      <DiscountsProvider>
                        <StatusBar style="dark" />
                        <QueryClientProvider client={queryClient}>
                          <Navigation />
                        </QueryClientProvider>
                      </DiscountsProvider>
                    </ForceUpdateApp>
                  </LocalesProvider>
                </Suspense>
              </ErrorBoundary>
            </AuthProvider>
          </ConfigProvider>
        </CartProvider>
      </SWRConfig>
    </DesignSystemProvider>
  )
}
